// Ranking and mixing for feed items.

const pad = (n) => String(n).padStart(2, '0');

export const monthDay = (d) => `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const isOnThisDay = (item, now) =>
  typeof item.date === 'string' && item.date.slice(5, 10) === monthDay(now);

// Small deterministic hash so evergreen items rotate daily but stay stable
// within a day (no reshuffling on every render).
const hash = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function scoreItem(item, now) {
  const dayKey = now.toISOString().slice(0, 10);
  let score = (item.weight ?? 1) * 10;
  // "On this day" is a small nudge (and a badge on the card), not a section.
  if (isOnThisDay(item, now)) score += 25;
  const ageDays = item.addedAt
    ? Math.max(0, (now.getTime() - item.addedAt) / DAY_MS)
    : null;
  if (item.personal) {
    // The reader's own reading and bookmarks lead the feed: +60 always,
    // up to +60 more for activity in the last 30 days.
    score += 60;
    if (ageDays != null && ageDays < 30) score += 60 * (1 - ageDays / 30);
  } else if (ageDays != null && ageDays < 7) {
    score += 40 - ageDays * 4;
  }
  if (item.type === 'event' && item.event) {
    // Live now first; upcoming within a week rises as it gets closer.
    const nowMs = now.getTime();
    const { start, end } = item.event;
    if (nowMs >= start && nowMs < end) score += 90;
    else if (start > nowMs) {
      const daysAway = (start - nowMs) / DAY_MS;
      if (daysAway < 7) score += 60 - daysAway * 6;
    } else score -= 30;
  }
  score += hash(`${dayKey}:${item.id}`) * 20;
  return score;
}

// Score every item, then mix: sources take turns (best remaining item of the
// best-scoring source that differs from the previous card), so no single
// section dominates the top of the feed.
export function rankFeed(items, now = new Date()) {
  const seen = new Set();
  const queues = new Map();
  items.forEach((it) => {
    if (!it || !it.id || seen.has(it.id)) return;
    seen.add(it.id);
    const scored = {
      ...it,
      onThisDay: isOnThisDay(it, now),
      score: scoreItem(it, now),
    };
    if (!queues.has(it.source)) queues.set(it.source, []);
    queues.get(it.source).push(scored);
  });
  // Curated (server) items: newest published batch first, and within a batch
  // the curators' order ("Ready for tonight"). Everything else by score.
  const curatedOrder = (a, b) =>
    Number.isInteger(a.rank) && Number.isInteger(b.rank)
      ? (b.publishedAt || 0) - (a.publishedAt || 0) || a.rank - b.rank
      : null;
  queues.forEach((q) =>
    q.sort((a, b) => curatedOrder(a, b) ?? b.score - a.score)
  );

  // Pinned items (server feed) go first, best score first; the rest mix.
  const out = [];
  queues.forEach((q, source) => {
    const pinned = q.filter((it) => it.pinned);
    if (!pinned.length) return;
    out.push(...pinned);
    const rest = q.filter((it) => !it.pinned);
    if (rest.length) queues.set(source, rest);
    else queues.delete(source);
  });
  out.sort((a, b) => b.score - a.score);

  let last = null;
  while (queues.size) {
    let pick = null;
    queues.forEach((q, source) => {
      if (source === last && queues.size > 1) return;
      if (!pick || q[0].score > queues.get(pick)[0].score) pick = source;
    });
    const q = queues.get(pick);
    out.push(q.shift());
    if (!q.length) queues.delete(pick);
    last = pick;
  }
  return out;
}
